const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
import { useEffect } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'astroturf/react'
import { parseQuery } from '@calendly/http-tools'

import {
  setPaymentData,
  setStripePaymentError,
  resetPayment,
  setStripePaymentElementReadyForCapture,
  setSaveFormDataForStripeRedirect
} from 'actions/payment'
import Badge from 'components/payment/stripe/badge'
import { useI18n } from 'locales'

import { PaymentElementField } from '../../payment/stripe/payment_element_field'
import {
  saveFormDataForStripeRedirectSelector,
  stripeClientSecretSelector,
  stripePaymentElementReadyForCaptureSelector
} from '../../../selectors/payment'

import { StripeRedirectHandler } from './stripe_redirect_handler'
import _MediumSpacing from "stripe_form-MediumSpacing.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/stripe_form.js?styleId=MediumSpacing";
import _StripeNote from "stripe_form-StripeNote.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/payment/stripe_form.js?styleId=StripeNote";


const MediumSpacing = /*#__PURE__*/styled('div', null, {
  displayName: "MediumSpacing",
  styles: _MediumSpacing
})

const StripeNote = /*#__PURE__*/styled('small', null, {
  displayName: "StripeNote",
  styles: _StripeNote,
  attrs: props => ({
    ...props,
    children: useI18n().i18n.t('booking.templates.payment.stripe_note')
  })
})

export const StripeForm = ({ registerApi }) => {
  const stripe = useStripe()
  const elements = useElements()
  const error = useSelector((state) => state.scheduling.payment.error)
  const dispatch = useDispatch()
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const paymentElementReadyForCapture = useSelector(stripePaymentElementReadyForCaptureSelector)
  const saveFormDataForStripeRedirect = useSelector(saveFormDataForStripeRedirectSelector)
  const query = parseQuery(window.location.search)
  const isRedirectFlow = query.payment_intent

  useEffect(() => {
    const paymentElement = elements?.getElement('payment')

    const handlePaymentMethodTypeChange = ({ value }) => {
      const selectedPaymentMethod = value.type

      if (selectedPaymentMethod !== 'card') {
        dispatch(setSaveFormDataForStripeRedirect(true))
      } else {
        dispatch(setSaveFormDataForStripeRedirect(false))
      }
    }

    if (paymentElement) {
      paymentElement.on('change', handlePaymentMethodTypeChange)
    }
  }, [dispatch, elements])

  useEffect(() => {
    const createPaymentElementPayment = async () => {
      const { error: validationError } = await elements.submit()

      if (validationError) {
        dispatch(setStripePaymentError(validationError))
        return false
      }

      if (paymentElementReadyForCapture) {
        // Do not confirm PaymentIntent again if it has already been set as ready for capture,
        // but was not captured by the backend because a non-payment related validation failed during scheduling
        dispatch(setPaymentData({
          payment_intent_client_secret: stripeClientSecret.value,
          payment_element_flow: true
        }))
        return true
      }

      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.href}${saveFormDataForStripeRedirect ? '' : '&closeOnLoad=true'}`
        },
        redirect: 'if_required'
      })

      if (result.error) {
        dispatch(setStripePaymentError(result.error))
        return false
      }

      if (result.paymentIntent.last_payment_error) {
        dispatch(setStripePaymentError({ code: 'payment_failed' }))
        return false
      }

      if (result.paymentIntent.status === 'requires_capture') {
        dispatch(setPaymentData({
          payment_intent_client_secret: result.paymentIntent.client_secret,
          payment_element_flow: true
        }))
        dispatch(setStripePaymentElementReadyForCapture())
        return true
      }

      return false
    }

    const handleCardAction = async (clientSecret) => {
      const result = await stripe.handleCardAction(clientSecret)
      if (result.error) {
        dispatch(setStripePaymentError(result.error))
        return false
      }

      dispatch(setPaymentData({
        id: result.paymentIntent.id,
        additional_payment_auth_done: true
      }))
      return true
    }

    if (!paymentElementReadyForCapture && !isRedirectFlow) dispatch(resetPayment())

    registerApi({
      createPayment: createPaymentElementPayment,
      handleCardAction
    })
  }, [
    dispatch,
    registerApi,
    stripe,
    elements,
    isRedirectFlow,
    paymentElementReadyForCapture,
    saveFormDataForStripeRedirect,
    stripeClientSecret
  ])

  if (isRedirectFlow) {
    return (
      <StripeRedirectHandler />
    )
  }

  return (
    <>
      <MediumSpacing>
        <PaymentElementField
          error={error}
        />
      </MediumSpacing>
      <StripeNote />
      <Badge />
    </>
  )
}
